import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import usePageTitle from '../../hooks/usePageTitle';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import useAccount from '../../hooks/useAccount';
import { useAxios } from '../../core/_axios';
import { ServiceSelector } from '../../components/ServiceSelector';
import { isPaymentOutOfDate, getAgencyName } from '../../utils/paymentUtils';

interface Service {
  unique_identifier: string;
  public_data: {
    deceased_person: {
      full_name: string;
      avatar_url?: string;
    };
    event_date?: string;
  };
  last_signature_at?: string;
  inserted_at?: string;
}

export function CreateQrCode() {
  const { currentUserAccountUniqueId, currentUser } = useAuth();
  const axios = useAxios();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { account } = useAccount({ axios, isForCurrentAccount: true });

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServiceUid, setSelectedServiceUid] = useState('');
  const [customSelectedService, setCustomSelectedService] = useState<string | null>(null);

  usePageTitle('Create QR Code');

  useEffect(() => {
    setPageHeader('Create QR Code');
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    if (!currentUserAccountUniqueId) {
      setLoadingServices(false);
      return;
    }

    const fetchServices = async () => {
      try {
        const response = await axios.get('/api/v1/app/authenticated/services');
        const servicesData = response.data.data?.services || response.data.services || [];

        // Sort by most recent signature first
        const sortedServices = servicesData.sort((a: Service, b: Service) => {
          if (!a.last_signature_at && !b.last_signature_at) return 0;
          if (!a.last_signature_at) return 1;
          if (!b.last_signature_at) return -1;
          return new Date(b.last_signature_at).getTime() - new Date(a.last_signature_at).getTime();
        });

        setServices(sortedServices);
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [currentUserAccountUniqueId, axios]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const payload: any = {
        base_slug: name.trim(),
        metadata: {
          name: name.trim()
        }
      };

      // Add link_config if a service is selected
      if (selectedServiceUid) {
        payload.link_config = {
          type: 'service',
          service_unique_identifier: selectedServiceUid
        };
      }

      const response = await axios.post('/api/v1/app/authenticated/current-account/qr-codes', payload);

      if (response.data.success) {
        navigate(`/qr-codes/${response.data.qr_code.unique_identifier}`);
      } else {
        setError(response.data.error || 'Failed to create QR code');
      }
    } catch (err: any) {
      console.error('Error creating QR code:', err);
      console.error('Response data:', err.response?.data);

      // Show detailed validation errors if available
      const errorMessage = err.response?.data?.error || 'Failed to create QR code';
      const errors = err.response?.data?.errors;

      if (errors && typeof errors === 'object') {
        const errorDetails = Object.entries(errors)
          .map(([field, msgs]: [string, any]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('; ');
        setError(`${errorMessage} - ${errorDetails}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Block access if payment is out of date
  const paymentOutOfDate = isPaymentOutOfDate(currentUser, account);

  if (paymentOutOfDate) {
    const agencyName = getAgencyName(currentUser, account);
    return (
      <div className="container-fixed max-w-2xl">
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <i className="ki-filled ki-information-circle text-6xl text-red-600 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Feature Restricted
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                Creating QR codes is currently restricted because payment for {agencyName} is past due.
              </p>
              <p className="text-sm text-gray-500">
                Please contact your agency administrator or SGB support to resolve this issue.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fixed max-w-2xl">
      <div className="card p-6">
        {error && (
          <div className="alert alert-error mb-6">
            <i className="ki-filled ki-information-2 text-lg"></i>
            <div className="flex flex-col gap-1">
              <div className="font-semibold">Error</div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label required">
                Name
              </label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Main Desk, Front Door, Chapel Entrance"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                What should this QR code be called?
              </p>
            </div>

            {loadingServices ? (
              <div className="flex justify-center py-6">
                <div className="spinner spinner-primary"></div>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">
                  Redirect To <span className="text-gray-500 text-sm font-normal">(optional)</span>
                </label>
                <ServiceSelector
                  services={services}
                  selectedServiceUid={selectedServiceUid}
                  onSelectService={setSelectedServiceUid}
                  onCustomServiceSelected={setCustomSelectedService}
                  onCustomServiceRemoved={() => {
                    setCustomSelectedService(null);
                    setSelectedServiceUid('');
                  }}
                  customSelectedService={customSelectedService}
                  showNotConfiguredOption={true}
                  readOnlyMode={false}
                />
                <p className="text-sm text-gray-500 mt-1">
                  You can configure this later if you prefer.
                </p>
              </div>
            )}

            <div className="alert alert-info flex items-start gap-3">
              <i className="ki-filled ki-information-2 text-lg flex-shrink-0"></i>
              <div>
                <div className="font-semibold mb-1">What happens next?</div>
                <div className="text-sm">
                  After creating your QR code, you can download it to print, choose where it sends people, and see how many people scan it.
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => navigate('/qr-codes')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !name.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner spinner-white spinner-sm"></span>
                  Creating...
                </>
              ) : (
                <>
                  Create QR Code
                  <i className="ki-filled ki-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
